import { Box, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

// ============= UTILITY FUNCTIONS =============

export const sanitizeHtml = (html) => {
    if (!html) return '';
    if (typeof html !== 'string') return String(html);
    
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    
    const dangerousTags = tmp.querySelectorAll('script, style');
    dangerousTags.forEach(el => el.remove());
    
    const allElements = tmp.querySelectorAll('*');
    allElements.forEach(el => {
        Array.from(el.attributes).forEach(attr => {
            if (attr.name.startsWith('on')) {
                el.removeAttribute(attr.name);
            }
        });
    });
    
    return tmp.innerHTML;
};

export const getHtmlContent = (content) => {
    if (!content) return '';
    if (typeof content !== 'string') return String(content);
    return sanitizeHtml(content);
};

export const countWords = (html) => {
    if (!html) return 0;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

export const calculateWordDiff = (originalHtml, currentHtml) => {
    const originalWords = countWords(originalHtml);
    const currentWords = countWords(currentHtml);
    const diff = currentWords - originalWords;
    return diff;
};

export const focusAndMoveCursorToEnd = (element) => {
    if (!element) return;
    element.focus();
    setTimeout(() => {
        if (element) {
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(element);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }, 0);
};

export const handlePasteWithFormatting = (e) => {
    e.preventDefault();
    const html = (e.clipboardData || window.clipboardData).getData('text/html');
    const plain = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertHTML', false, html || plain);
};

// ============= LANGUAGE OPTIONS =============
export const AVAILABLE_LANGUAGES = [
    { code: 'en', label: 'EN', name: 'English' },
    { code: 'es', label: 'ES', name: 'Spanish' },
    { code: 'ja', label: 'JA', name: 'Japanese' },
    { code: 'ko', label: 'KO', name: 'Korean' },
    { code: 'th', label: 'TH', name: 'Thai' },
    { code: 'vi', label: 'VI', name: 'Vietnamese' },
];

// ============= STYLED COMPONENTS =============

export const EditableContainer = styled(Box)(({ isEditMode, isHovered, isEditing }) => ({
    position: 'relative',
    cursor: isEditMode && !isEditing ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    borderRadius: '4px',
    padding: isEditing ? '8px' : '0px',
    border: isEditing 
        ? '2px solid #0097b2' 
        : isHovered && isEditMode 
            ? '2px dashed #0097b2' 
            : '2px solid transparent',
    backgroundColor: isEditing ? '#F5F9FF' : 'transparent',
    '&:hover': {
        border: isEditMode && !isEditing ? '2px dashed #0097b2' : undefined,
    },
}));

// Reset button - Icon only in top-right corner
export const ResetIconButton = styled(IconButton)({
    position: 'absolute',
    top: '4px',
    right: '4px',
    padding: '6px',
    color: '#666',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    zIndex: 10,
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        color: '#333',
        transform: 'rotate(180deg)',
    },
});

// Word count - Inside border in orange (shows changed words count)
export const WordCountDisplay = styled(Box)(() => ({
    position: 'absolute',
    top: '8px',
    left: '8px',
    color: '#EE6D29',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(238, 109, 41, 0.1)',
    border: '1px solid #EE6D29',
    zIndex: 5,
}));

export const ActionButtonsBox = styled(Box)({
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: '12px',
    marginTop: '8px',
});

export const ActionButton = styled(Button)(({ actionType, disabled }) => ({
    borderRadius: '6px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '6px 20px',
    fontSize: '13px',
    minWidth: '80px',
    height: '32px',
    border: 'none',
    backgroundColor: actionType === 'save' ? '#EE6D29' : '#e0e0e0',
    color: actionType === 'save' ? '#fff' : '#666',
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: disabled 
            ? (actionType === 'save' ? '#EE6D29' : '#e0e0e0')
            : (actionType === 'save' ? '#d55a1f' : '#d5d5d5'),
        transform: disabled ? 'none' : 'translateY(-1px)',
        boxShadow: disabled ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
    },
}));

export const editableStyles = {
    outline: 'none',
    caretColor: '#333',
    '&:focus': {
        outline: 'none',
    },
    minHeight: '40px',
    padding: '4px',
    paddingTop: '24px', // Space for word count
    wordBreak: 'break-word',
    '& ul, & ol': {
        margin: '8px 0',
        paddingLeft: '24px',
    },
    '& li': {
        margin: '4px 0',
    },
    '& strong, & b': {
        fontWeight: 'bold',
    },
    '& em, & i': {
        fontStyle: 'italic',
    },
    '& u': {
        textDecoration: 'underline',
    },
    '& p': {
        margin: '8px 0',
    },
};
